import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, App, Space, Select, Tag, Image, message as antdMessage, Popconfirm, DatePicker, Switch, InputNumber, Row, Col, Divider } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined, QrcodeOutlined, ScanOutlined, PrinterOutlined, ReloadOutlined, ClearOutlined, UserOutlined, HomeOutlined, CarOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import request from '../utils/request';
import { getCurrentUser } from '../utils/user';

const VISIT_PURPOSES = ['探亲访友', '装修施工', '快递配送', '商务洽谈', '外卖送餐', '其他'];
const VISIT_TIME_SLOTS = [
  { value: '上午 08:00-12:00', start: '08:00', end: '12:00', label: '上午 08:00-12:00' },
  { value: '下午 14:00-18:00', start: '14:00', end: '18:00', label: '下午 14:00-18:00' },
  { value: '晚间 18:00-21:00', start: '18:00', end: '21:00', label: '晚间 18:00-21:00' }
];

const LABEL_WIDTH = 80;
const FilterItem = ({ label, children }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
    <span style={{ display: 'inline-block', width: LABEL_WIDTH, textAlign: 'right', color: '#555', fontSize: 14, whiteSpace: 'nowrap' }}>{label}:</span>
    {children}
  </div>
);

const Appointment = () => {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const currentUser = getCurrentUser();
  const isSuperAdmin = Boolean(currentUser.isSuperAdmin);
  const fixedCommunityId = !isSuperAdmin ? currentUser.communityId : null;
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [detailVisible, setDetailVisible] = useState(false);
  const [qrcodeVisible, setQrcodeVisible] = useState(false);
  const [qrcodeRecord, setQrcodeRecord] = useState(null);
  const [checkinLoading, setCheckinLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [form] = Form.useForm();
  const [editingRecord, setEditingRecord] = useState(null);
  const [viewRecord, setViewRecord] = useState(null);

  const [filter, setFilter] = useState({ status: '', visitorName: '', visitorPhone: '', dateRange: null, communityId: fixedCommunityId });
  const [communities, setCommunities] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });

  const [createVisible, setCreateVisible] = useState(false);
  const [createForm] = Form.useForm();
  const [createLoading, setCreateLoading] = useState(false);
  const [roomSelection, setRoomSelection] = useState({ communityId: null, buildingId: null, unitId: null, roomId: null });
  const [cascadeData, setCascadeData] = useState({ communities: [], buildings: [], units: [], rooms: [] });
  const [cascadeLoading, setCascadeLoading] = useState({ buildings: false, units: false, rooms: false });
  const [selectedRoomInfo, setSelectedRoomInfo] = useState(null);

  useEffect(() => {
    fetchCommunities();
    fetchData();
  }, []);

  const fetchCommunities = async () => {
    try {
      const res = await request.get('/community', { params: { page: 1, pageSize: 999 } });
      if (res.code === 200) setCommunities(res.data.list || []);
    } catch (error) {
      console.error('Failed to fetch communities:', error);
    }
  };

  const fetchData = async (params = {}) => {
    setLoading(true);
    try {
      const page = params.page ?? pagination.current;
      const pageSize = params.pageSize ?? pagination.pageSize;
      const merged = { page, pageSize };
      if (params.status !== undefined) {
        if (params.status) merged.status = params.status;
      } else if (filter.status) {
        merged.status = filter.status;
      }
      if (params.visitorName !== undefined) {
        if (params.visitorName) merged.visitorName = params.visitorName;
      } else if (filter.visitorName.trim()) {
        merged.visitorName = filter.visitorName.trim();
      }
      if (params.visitorPhone !== undefined) {
        if (params.visitorPhone) merged.visitorPhone = params.visitorPhone;
      } else if (filter.visitorPhone.trim()) {
        merged.visitorPhone = filter.visitorPhone.trim();
      }
      if (params.visitDate !== undefined) {
        if (params.visitDate) merged.visitDate = params.visitDate;
      } else if (filter.dateRange && filter.dateRange.length === 2) {
        merged.visitDate = filter.dateRange[0].format('YYYY-MM-DD');
      }
      if (!isSuperAdmin) {
        merged.communityId = fixedCommunityId;
      } else if (params.communityId !== undefined) {
        if (params.communityId) merged.communityId = params.communityId;
      } else if (filter.communityId) {
        merged.communityId = filter.communityId;
      }
      const res = await request.get('/appointment/list', { params: merged });
      if (res.code === 200) {
        setData(res.data.list || []);
        setPagination({
          current: res.data.page || page,
          pageSize: res.data.pageSize || pageSize,
          total: res.data.total || 0
        });
      }
    } catch (error) {
      message.error('获取数据失败');
    } finally {
      setLoading(false);
    }
  };

  const applyFilter = () => {
    setPagination(p => ({ ...p, current: 1 }));
    setSelectedRowKeys([]);
    const params = { page: 1, pageSize: pagination.pageSize };
    if (filter.status) params.status = filter.status;
    if (filter.visitorName.trim()) params.visitorName = filter.visitorName.trim();
    if (filter.visitorPhone.trim()) params.visitorPhone = filter.visitorPhone.trim();
    if (filter.dateRange && filter.dateRange.length === 2) {
      params.visitDate = filter.dateRange[0].format('YYYY-MM-DD');
    }
    if (isSuperAdmin && filter.communityId) params.communityId = filter.communityId;
    fetchData(params);
  };

  const resetFilter = () => {
    setFilter({ status: '', visitorName: '', visitorPhone: '', dateRange: null, communityId: fixedCommunityId });
    setPagination(p => ({ ...p, current: 1 }));
    setSelectedRowKeys([]);
    fetchData({ page: 1, pageSize: pagination.pageSize });
  };

  const handleTableChange = (pag) => {
    setPagination(p => ({ ...p, current: pag.current, pageSize: pag.pageSize }));
    fetchData({ page: pag.current, pageSize: pag.pageSize });
  };

  const handleView = (record) => {
    setViewRecord(record);
    setDetailVisible(true);
  };

  const handleShowQrcode = (record) => {
    setQrcodeRecord(record);
    setQrcodeVisible(true);
  };

  const handlePrint = (record) => {
    const ids = record ? [record.id] : selectedRowKeys;
    if (ids.length === 0) {
      antdMessage.warning('请先选择要打印的预约');
      return;
    }
    navigate(`/print?ids=${ids.join(',')}`);
  };

  const handleRegenerateQrcodes = async () => {
    try {
      const res = await request.post('/appointment/regenerate-qrcodes');
      if (res.code === 200) {
        antdMessage.success(`已重新生成 ${res.data} 个二维码`);
        fetchData();
      } else {
        antdMessage.error(res.message || '操作失败');
      }
    } catch (err) {
      antdMessage.error(err?.message || '网络错误');
    }
  };

  const handleCleanupPending = async () => {
    const days = 3;
    try {
      const res = await request.post(`/appointment/cleanup-pending?days=${days}`);
      if (res.code === 200) {
        antdMessage.success(`已清理 ${res.data} 条 ${days} 天前的待审核预约`);
        fetchData();
      } else {
        antdMessage.error(res.message || '操作失败');
      }
    } catch (err) {
      antdMessage.error(err?.message || '网络错误');
    }
  };

  const [cleanupCount, setCleanupCount] = useState(0);
  useEffect(() => {
    request.get(`/appointment/cleanup-pending/count?days=3`)
      .then(res => { if (res.code === 200) setCleanupCount(res.data || 0); })
      .catch(() => {});
  }, [data]);

  const handleCheckin = async (record) => {
    const user = getCurrentUser();
    setCheckinLoading(true);
    try {
      const res = await request.post('/checkin/scan', {
        qrData: String(record.id),
        operatorId: user.id || null,
        operatorName: user.name || '管理员'
      });
      if (res.code === 200) {
        antdMessage.success(`已核销：${res.data.visitorName}`);
        fetchData();
      } else {
        antdMessage.error(res.message || '核销失败');
      }
    } catch (err) {
      antdMessage.error(err?.message || '核销失败');
    } finally {
      setCheckinLoading(false);
    }
  };

  const handleApprove = async (id) => {
    try {
      await request.put('/appointment/' + id + '/approve');
      message.success('审核通过');
      fetchData();
    } catch (error) {
      message.error('审核失败');
    }
  };

  const handleReject = async (id) => {
    try {
      await request.put('/appointment/' + id + '/reject');
      message.success('已拒绝');
      fetchData();
    } catch (error) {
      message.error('操作失败');
    }
  };

  const handleOpenCreate = async () => {
    setCreateVisible(true);
    createForm.resetFields();
    createForm.setFieldsValue({ visitorCount: 1, hasVehicle: false });
    setRoomSelection({ communityId: null, buildingId: null, unitId: null, roomId: null });
    setCascadeData({ communities: [], buildings: [], units: [], rooms: [] });
    setSelectedRoomInfo(null);
    setCascadeLoading({ buildings: false, units: false, rooms: false });
    await fetchCascadeCommunities();
  };

  const fetchCascadeCommunities = async () => {
    try {
      const res = await request.get('/community', { params: { pageSize: 1000 } });
      if (res.code === 200) setCascadeData(prev => ({ ...prev, communities: res.data.list || [] }));
    } catch (err) {
      console.error(err);
    }
  };

  const fetchBuildings = async (communityId) => {
    setCascadeLoading(prev => ({ ...prev, buildings: true }));
    try {
      const res = await request.get('/building', { params: { communityId, pageSize: 1000 } });
      if (res.code === 200) setCascadeData(prev => ({ ...prev, buildings: res.data.list || [] }));
    } catch (err) {
      console.error(err);
    } finally {
      setCascadeLoading(prev => ({ ...prev, buildings: false }));
    }
  };

  const fetchUnits = async (buildingId) => {
    setCascadeLoading(prev => ({ ...prev, units: true }));
    try {
      const res = await request.get('/unit', { params: { buildingId, pageSize: 1000 } });
      if (res.code === 200) setCascadeData(prev => ({ ...prev, units: res.data.list || [] }));
    } catch (err) {
      console.error(err);
    } finally {
      setCascadeLoading(prev => ({ ...prev, units: false }));
    }
  };

  const fetchRooms = async (unitId) => {
    setCascadeLoading(prev => ({ ...prev, rooms: true }));
    try {
      const res = await request.get('/room', { params: { unitId, pageSize: 1000 } });
      if (res.code === 200) setCascadeData(prev => ({ ...prev, rooms: res.data.list || [] }));
    } catch (err) {
      console.error(err);
    } finally {
      setCascadeLoading(prev => ({ ...prev, rooms: false }));
    }
  };

  const onCommunityChange = (v) => {
    setRoomSelection({ communityId: v, buildingId: null, unitId: null, roomId: null });
    setCascadeData(prev => ({ ...prev, buildings: [], units: [], rooms: [] }));
    setSelectedRoomInfo(null);
    createForm.setFieldValue('roomId', undefined);
    if (v) fetchBuildings(v);
  };

  const onBuildingChange = (v) => {
    setRoomSelection(prev => ({ ...prev, buildingId: v, unitId: null, roomId: null }));
    setCascadeData(prev => ({ ...prev, units: [], rooms: [] }));
    setSelectedRoomInfo(null);
    createForm.setFieldValue('roomId', undefined);
    if (v) fetchUnits(v);
  };

  const onUnitChange = (v) => {
    setRoomSelection(prev => ({ ...prev, unitId: v, roomId: null }));
    setCascadeData(prev => ({ ...prev, rooms: [] }));
    setSelectedRoomInfo(null);
    createForm.setFieldValue('roomId', undefined);
    if (v) fetchRooms(v);
  };

  const onRoomChange = (v, option) => {
    setRoomSelection(prev => ({ ...prev, roomId: v }));
    setSelectedRoomInfo(option?.data || null);
    createForm.setFieldValue('roomId', v);
  };

  const onVisitTimeChange = (v) => {
    const slot = VISIT_TIME_SLOTS.find(s => s.value === v);
    if (slot) {
      createForm.setFieldsValue({ visitTimeStart: slot.start, visitTimeEnd: slot.end });
    } else {
      createForm.setFieldsValue({ visitTimeStart: undefined, visitTimeEnd: undefined });
    }
  };

  const handleCreate = async (values) => {
    setCreateLoading(true);
    try {
      const payload = {
        visitor_name: values.visitorName,
        visitor_phone: values.visitorPhone,
        visitor_count: values.visitorCount || 1,
        purpose: values.purpose,
        visit_date: values.visitDate.format('YYYY-MM-DD'),
        visit_time: values.visitTime,
        visit_time_start: values.visitTimeStart,
        visit_time_end: values.visitTimeEnd,
        room_id: values.roomId,
        remark: values.remark,
        has_vehicle: values.hasVehicle || false,
        vehicle_plate: values.vehiclePlate,
        vehicle_color: values.vehicleColor,
        vehicle_brand: values.vehicleBrand
      };
      const res = await request.post('/appointment/create', payload);
      if (res.code === 200) {
        antdMessage.success('预约创建成功,业主将收到审核通知');
        setCreateVisible(false);
        fetchData();
      } else {
        antdMessage.error(res.message || '创建失败');
      }
    } catch (err) {
      antdMessage.error(err?.response?.data?.message || err?.message || '创建失败');
    } finally {
      setCreateLoading(false);
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    { title: '访客姓名', dataIndex: 'visitor_name', key: 'visitor_name' },
    { title: '访客电话', dataIndex: 'visitor_phone', key: 'visitor_phone' },
    { title: '访问房号', dataIndex: 'room_info', key: 'room_info' },
    { title: '访问日期', dataIndex: 'visit_date', key: 'visit_date' },
    { title: '访问时间', dataIndex: 'visit_time', key: 'visit_time' },
    { title: '状态', dataIndex: 'status', key: 'status', render: (text) => {
      const colors = {
        pending: 'orange',
        approved: 'green',
        rejected: 'red',
        checked_in: 'blue',
        cancelled: 'default',
        expired: 'gray'
      };
      const labels = {
        pending: '待审核',
        approved: '已通过',
        rejected: '已拒绝',
        checked_in: '已核销',
        cancelled: '已取消',
        expired: '已过期'
      };
      return <Tag color={colors[text]}>{labels[text] || text}</Tag>;
    }},
    {
      title: '操作',
      key: 'action',
      width: 360,
      render: (_, record) => (
        <Space>
          <Button type="link" icon={<EyeOutlined />} onClick={() => handleView(record)}>查看</Button>
          {record.status === 'approved' && record.qr_code && (
            <Button type="link" icon={<QrcodeOutlined />} onClick={() => handleShowQrcode(record)}>二维码</Button>
          )}
          {record.status === 'approved' && (
            <>
              <Button type="link" icon={<PrinterOutlined />} onClick={() => handlePrint(record)}>打印</Button>
              <Button type="link" icon={<ScanOutlined />} loading={checkinLoading} onClick={() => handleCheckin(record)}>核销</Button>
            </>
          )}
          {record.status === 'pending' && (
            <>
              <Button type="link" onClick={() => handleApprove(record.id)}>通过</Button>
              <Button type="link" danger onClick={() => handleReject(record.id)}>拒绝</Button>
            </>
          )}
        </Space>
      )
    }
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Space>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenCreate}>添加预约</Button>
          {selectedRowKeys.length > 0 && (
            <Button icon={<PrinterOutlined />} onClick={() => handlePrint()}>
              批量打印 ({selectedRowKeys.length})
            </Button>
          )}
          <Popconfirm
            title="批量重新生成二维码"
            description={'将用当前域名重新生成所有『已通过』状态的预约二维码。此操作不可撤销，是否继续？'}
            onConfirm={handleRegenerateQrcodes}
            okText="确认生成"
            cancelText="取消"
          >
            <Button icon={<ReloadOutlined />}>批量重新生成二维码</Button>
          </Popconfirm>
          <Popconfirm
            title="清理 3 天前的待审核预约"
            description={`将永久删除 ${cleanupCount} 条 3 天前提交仍未审核的预约，此操作不可撤销，是否继续？`}
            onConfirm={handleCleanupPending}
            okText="确认清理"
            cancelText="取消"
            disabled={cleanupCount === 0}
          >
            <Button icon={<ClearOutlined />} danger disabled={cleanupCount === 0}>
              清理 3 天前待审核 {cleanupCount > 0 ? `(${cleanupCount})` : ''}
            </Button>
          </Popconfirm>
        </Space>
      </div>

      <div style={{ marginBottom: 16, padding: 16, background: '#fafafa', borderRadius: 6 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center' }}>
          <FilterItem label="小区">
            <Select
              placeholder={isSuperAdmin ? '全部小区' : '本小区'}
              value={filter.communityId || undefined}
              onChange={v => setFilter({ ...filter, communityId: v })}
              style={{ width: 180 }}
              disabled={!isSuperAdmin}
              allowClear={isSuperAdmin}
            >
              {communities.map(c => <Select.Option key={c.id} value={c.id}>{c.name}</Select.Option>)}
            </Select>
          </FilterItem>
          <FilterItem label="状态">
            <Select
              placeholder="全部状态"
              value={filter.status || undefined}
              onChange={v => setFilter({ ...filter, status: v })}
              style={{ width: 180 }}
              allowClear
            >
              <Select.Option value="pending">待审核</Select.Option>
              <Select.Option value="approved">已通过</Select.Option>
              <Select.Option value="rejected">已拒绝</Select.Option>
              <Select.Option value="checked_in">已核销</Select.Option>
              <Select.Option value="cancelled">已取消</Select.Option>
              <Select.Option value="expired">已过期</Select.Option>
            </Select>
          </FilterItem>
          <FilterItem label="访客姓名">
            <Input
              placeholder="输入访客姓名"
              value={filter.visitorName}
              onChange={e => setFilter({ ...filter, visitorName: e.target.value })}
              onPressEnter={applyFilter}
              style={{ width: 180 }}
              allowClear
            />
          </FilterItem>
          <FilterItem label="访客手机号">
            <Input
              placeholder="输入访客手机号"
              value={filter.visitorPhone}
              onChange={e => setFilter({ ...filter, visitorPhone: e.target.value })}
              onPressEnter={applyFilter}
              style={{ width: 180 }}
              allowClear
            />
          </FilterItem>
          <FilterItem label="访问日期">
            <DatePicker
              value={filter.dateRange}
              onChange={v => setFilter({ ...filter, dateRange: v ? [v, v] : null })}
              placeholder="选择日期"
              style={{ width: 180 }}
              allowClear
            />
          </FilterItem>
          <Button type="primary" onClick={applyFilter}>筛选</Button>
          <Button onClick={resetFilter}>重置</Button>
        </div>
      </div>

      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showTotal: (t) => `共 ${t} 条`,
          showSizeChanger: true,
          showQuickJumper: true,
          pageSizeOptions: ['10', '20', '50', '100']
        }}
        onChange={handleTableChange}
        rowSelection={{
          selectedRowKeys,
          onChange: setSelectedRowKeys,
          preserveSelectedRowKeys: true
        }}
      />

      <Modal
        title="预约详情"
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={null}
      >
        {viewRecord && (
          <div>
            <h4>访客信息</h4>
            <p>姓名: {viewRecord.visitor_name}</p>
            <p>电话: {viewRecord.visitor_phone}</p>
            <p>人数: {viewRecord.visitor_count}人</p>

            <h4>访问信息</h4>
            <p>房号: {viewRecord.room_info}</p>
            <p>日期: {viewRecord.visit_date}</p>
            <p>时间: {viewRecord.visit_time}</p>
            <p>目的: {viewRecord.purpose}</p>

            <h4>状态</h4>
            <Tag color={
              viewRecord.status === 'pending' ? 'orange'
              : viewRecord.status === 'approved' ? 'green'
              : viewRecord.status === 'checked_in' ? 'blue'
              : viewRecord.status === 'rejected' ? 'red'
              : 'default'
            }>
              {viewRecord.status === 'pending' ? '待审核'
                : viewRecord.status === 'approved' ? '已通过'
                : viewRecord.status === 'checked_in' ? '已核销'
                : viewRecord.status === 'rejected' ? '已拒绝'
                : '未知'}
            </Tag>

            {viewRecord.status === 'approved' && viewRecord.qr_code && (
              <div style={{ marginTop: 16, textAlign: 'center' }}>
                <Button type="primary" icon={<QrcodeOutlined />} onClick={() => handleShowQrcode(viewRecord)}>
                  查看访客二维码
                </Button>
              </div>
            )}
          </div>
        )}
      </Modal>

      <Modal
        title="访客二维码"
        open={qrcodeVisible}
        onCancel={() => setQrcodeVisible(false)}
        footer={null}
        width={360}
      >
        {qrcodeRecord && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ marginBottom: 12, fontSize: 16, fontWeight: 'bold' }}>{qrcodeRecord.visitor_name}</div>
            <div style={{ marginBottom: 12, color: '#666' }}>{qrcodeRecord.visit_date} {qrcodeRecord.visit_time}</div>
            {qrcodeRecord.qr_code ? (
              <Image
                src={qrcodeRecord.qr_code}
                alt="访客二维码"
                style={{ maxWidth: 280, width: '100%' }}
                preview={false}
              />
            ) : (
              <Tag color="red">暂无二维码</Tag>
            )}
            <div style={{ marginTop: 12, fontSize: 12, color: '#999' }}>请访客出示此码，前台扫码核销</div>
          </div>
        )}
      </Modal>

      <Modal
        title={<span><PlusOutlined /> 添加预约</span>}
        open={createVisible}
        onCancel={() => !createLoading && setCreateVisible(false)}
        footer={null}
        width={760}
        destroyOnClose
        maskClosable={false}
      >
        <Form
          form={createForm}
          layout="vertical"
          onFinish={handleCreate}
          initialValues={{ visitorCount: 1, hasVehicle: false }}
        >
          <Divider orientation="left" plain style={{ margin: '0 0 16px 0' }}>
            <span style={{ color: '#1677ff' }}><UserOutlined /> 访客信息</span>
          </Divider>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="访客姓名" name="visitorName" rules={[{ required: true, message: '请输入访客姓名' }]}>
                <Input placeholder="请输入访客姓名" maxLength={20} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="访客手机号"
                name="visitorPhone"
                rules={[
                  { required: true, message: '请输入手机号' },
                  { pattern: /^1[3-9]\d{9}$/, message: '手机号格式不正确' }
                ]}
              >
                <Input placeholder="请输入11位手机号" maxLength={11} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="来访人数" name="visitorCount">
                <InputNumber min={1} max={20} style={{ width: '100%' }} placeholder="1" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="来访目的" name="purpose" rules={[{ required: true, message: '请选择来访目的' }]}>
                <Select placeholder="请选择来访目的">
                  {VISIT_PURPOSES.map(p => <Select.Option key={p} value={p}>{p}</Select.Option>)}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" plain style={{ margin: '8px 0' }}>
            <span style={{ color: '#1677ff' }}><HomeOutlined /> 访问信息</span>
          </Divider>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="访问日期" name="visitDate" rules={[{ required: true, message: '请选择访问日期' }]}>
                <DatePicker
                  style={{ width: '100%' }}
                  disabledDate={(d) => d && d.isBefore(new Date(), 'day')}
                  placeholder="选择日期"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="访问时段" name="visitTime" rules={[{ required: true, message: '请选择时段' }]}>
                <Select placeholder="选择时段" onChange={onVisitTimeChange}>
                  {VISIT_TIME_SLOTS.map(s => <Select.Option key={s.value} value={s.value}>{s.label}</Select.Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="具体时间" style={{ marginBottom: 0 }}>
                <Space.Compact style={{ width: '100%' }}>
                  <Form.Item name="visitTimeStart" noStyle>
                    <Input style={{ width: '50%', textAlign: 'center' }} placeholder="开始" disabled />
                  </Form.Item>
                  <Input style={{ width: 30, textAlign: 'center', pointerEvents: 'none', background: '#fff' }} placeholder="~" disabled />
                  <Form.Item name="visitTimeEnd" noStyle>
                    <Input style={{ width: 'calc(50% - 30px)', textAlign: 'center' }} placeholder="结束" disabled />
                  </Form.Item>
                </Space.Compact>
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" plain style={{ margin: '8px 0' }}>
            <span style={{ color: '#1677ff' }}><HomeOutlined /> 访问房号</span>
          </Divider>
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item label="小区" required>
                <Select
                  placeholder="选择小区"
                  value={roomSelection.communityId || undefined}
                  onChange={onCommunityChange}
                  allowClear
                  showSearch
                  optionFilterProp="children"
                >
                  {cascadeData.communities.map(c => <Select.Option key={c.id} value={c.id}>{c.name}</Select.Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="楼栋" required>
                <Select
                  placeholder="选择楼栋"
                  value={roomSelection.buildingId || undefined}
                  onChange={onBuildingChange}
                  loading={cascadeLoading.buildings}
                  disabled={!roomSelection.communityId}
                  allowClear
                  showSearch
                  optionFilterProp="children"
                >
                  {cascadeData.buildings.map(b => <Select.Option key={b.id} value={b.id}>{b.name}</Select.Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="单元" required>
                <Select
                  placeholder="选择单元"
                  value={roomSelection.unitId || undefined}
                  onChange={onUnitChange}
                  loading={cascadeLoading.units}
                  disabled={!roomSelection.buildingId}
                  allowClear
                  showSearch
                  optionFilterProp="children"
                >
                  {cascadeData.units.map(u => <Select.Option key={u.id} value={u.id}>{u.name}</Select.Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="房号" name="roomId" rules={[{ required: true, message: '请选择房号' }]}>
                <Select
                  placeholder="选择房号"
                  value={roomSelection.roomId || undefined}
                  onChange={onRoomChange}
                  loading={cascadeLoading.rooms}
                  disabled={!roomSelection.unitId}
                  showSearch
                  optionFilterProp="children"
                >
                  {cascadeData.rooms.map(r => (
                    <Select.Option key={r.id} value={r.id} data={r}>
                      {r.number}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          {selectedRoomInfo && (
            <div style={{ marginTop: -8, marginBottom: 16, padding: 10, background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 6, fontSize: 13 }}>
              <InfoCircleOutlined style={{ color: '#52c41a', marginRight: 6 }} />
              已选房号: <b>
                {cascadeData.communities.find(c => c.id === roomSelection.communityId)?.name}
                {' '}
                {cascadeData.buildings.find(b => b.id === roomSelection.buildingId)?.name}
                {' '}
                {cascadeData.units.find(u => u.id === roomSelection.unitId)?.name}
                {' '}
                {selectedRoomInfo.number}
              </b>
              <span style={{ color: '#999', marginLeft: 12 }}>业主将收到审核通知</span>
            </div>
          )}

          <Divider orientation="left" plain style={{ margin: '8px 0' }}>
            <span style={{ color: '#1677ff' }}><CarOutlined /> 车辆信息</span>
          </Divider>
          <Form.Item label="是否有车辆" name="hasVehicle" valuePropName="checked" style={{ marginBottom: 12 }}>
            <Switch checkedChildren="是" unCheckedChildren="否" />
          </Form.Item>
          <Form.Item shouldUpdate={(prev, curr) => prev.hasVehicle !== curr.hasVehicle} style={{ marginBottom: 16 }}>
            {({ getFieldValue }) =>
              getFieldValue('hasVehicle') ? (
                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item label="车牌号" name="vehiclePlate" rules={[{ required: true, message: '请输入车牌号' }]}>
                      <Input placeholder="如:京A12345" />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label="车辆颜色" name="vehicleColor">
                      <Input placeholder="如:白色" />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label="车辆品牌" name="vehicleBrand">
                      <Input placeholder="如:丰田" />
                    </Form.Item>
                  </Col>
                </Row>
              ) : null
            }
          </Form.Item>

          <Form.Item label="备注" name="remark">
            <Input.TextArea rows={3} placeholder="可选,如:同行人员信息、特殊说明等" maxLength={200} showCount />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, marginTop: 16, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setCreateVisible(false)} disabled={createLoading}>取消</Button>
              <Button type="primary" htmlType="submit" loading={createLoading} icon={<PlusOutlined />}>
                提交预约
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Appointment;
